import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { formatPhoneNumber } from '../utils/phone'
import { Person } from './Person'
import { FKType } from './SQLBuilder'
import { Sortable } from './Sortable'

type ContactConstructorArgs = ConstructorArgs<() => Contact>

export class Contact extends Person implements Sortable {
    readonly recordType = 'contact'

    readonly iSortable = true

    first: string | undefined
    last: string | undefined
    private _externalId: string | undefined
    modifiedDate: moment.Moment | undefined
    creationDate: moment.Moment | undefined

    constructor(data?: Partial<ContactConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.name = data.name ?? undefined
        this.first = data.first ?? undefined
        this.last = data.last ?? undefined
        this.notes = data.notes ?? undefined
        this.entity = data.entity ?? undefined
        this.email = data.email ?? undefined
        this.phone = data.phone ?? undefined
        this.label = data.label ?? undefined
        this.line1 = data.line1 ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this._externalId = data.external_id ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.creationDate = this.propertyFromTimestampArg(data.creation_date)
        this.modifiedDate = this.propertyFromTimestampArg(data.modified_date)
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
    }

    clone(overrides?: Partial<ContactConstructorArgs>): Contact {
        return new Contact({ ...this.serializeForClone(), ...overrides })
    }

    get externalId(): string | undefined {
        return this._externalId
    }

    set name(name: string | undefined) {
        this.name = name

        if (name && name.includes(' ')) {
            this.first = name.substring(0, name.indexOf(' '))
            this.last = name.substring(name.indexOf(' ') + 1)
        }
        else {
            this.first = name
            this.last = undefined
        }
    }

    get description(): string | undefined {
        if (this.phone) return formatPhoneNumber(this.phone)
        return this.email
    }

    set email(email: string | undefined) {
        this.email = email

        if (this.externalId) this.recalcId()
    }

    set externalId(externalId: string | undefined) {
        this._externalId = externalId
        if (this.phone || this.email) this.recalcId()
    }

    set phone(phone: string | undefined) {
        this.phone = phone

        if (this.externalId) this.recalcId()
    }

    private recalcId() {
        this.id = this.phone ? this.phone.replace(/\D+/g, '') : ''
        if (!this.id) this.id = this.email
        if (this.externalId) this.id = `${this.externalId}${this.id}`
    }

    toString(): string {
        const string = formatPhoneNumber(this.phone)
        return string ?? ''
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.modifiedDate) this.modifiedDate = moment(data.modifiedDate)
        if (data.creationDate) this.creationDate = moment(data.creationDate)
    }

    static fk: FKType = {
        ...Person.fk,
    }

    static dataType(): DataType {
        return 'contacts'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['owner'] = PSApplication.userId()
        dict['creation_date'] = this.creationDate ? this.creationDate.format('YYYY-MM-DD') : ''
        dict['modified_date'] = this.modifiedDate ? this.modifiedDate.format('YYYY-MM-DD') : ''
        dict['notes'] = this.notes
        dict['industry'] = this.industry?.id ?? ''
        dict['external_id'] = this.externalId
        return dict
    }
}
